import { defaultActionButtonStyle } from '@cod/config-schema';
import { box, button, checkbox, column, link, markdown, row, styled, text, } from '@tiendanube/nube-sdk-ui';
import { customImageNode, customTextNode, discountNode, orderSummaryNode, shippingNode, tokens, totalsNode, } from './blocks.js';
import { linkButtonNode, whatsappHref, whatsappNode } from './actions.js';
import { chainIndex, coverageOptions, coverageValue } from './coverage.js';
import { offersNode } from './offers.js';
import { fieldNode } from './fields.js';
import { thankYouMarkdown, thankYouTokens, thankYouTree } from './thankYou.js';
import { buttonIcon, homeIcon } from './icons.js';
import { withIdentity } from './identity.js';
import { buttonAnimationCss, buttonLabelCss, buttonSubtitleCss, codButtonCss, codCloseCss, consentBoxCss, consentRowCss, consentTextCss, termsLinkCss, formButtonLabelCss, messageTextCss, codStyles, } from './theme.js';
/**
 * `buildCodForm` — the whole form as one pure `NubeComponent` tree.
 *
 * 🚨 **This assembly belongs here and NOT in `apps/storefront-app`.** It is the thing that makes
 * the admin live preview match the storefront (`MIGRATION.md` §6.1). If the storefront app composed
 * the header, fields and submit itself, the admin would have to compose them again — and the two
 * would drift the first time anyone touched either. The app's job is state, events and network; the
 * form's *structure* is here, so both consumers get it by construction.
 *
 *     storefront:  App(nube) → buildCodForm(…) → nube.render(slot, tree)  → host renders
 *     admin:       LivePreview → buildCodForm(…) → renderNubeTree(tree)   → Nimbus components
 *
 * Pure throughout: no DOM, no `nube`, no `fetch`, no state of its own. `preview` mode simply passes
 * no handlers, which is what makes the admin copy inert without the builder knowing about the admin.
 */
/**
 * ✅ **Every block the legacy renderer produced is now ported**, quantity offers included.
 *
 * The remaining gaps are OUTSIDE this builder and tracked in `migrationPlan.md` Phase 8:
 *
 *  - `config.mode === 'embedded'` — the storefront app only renders the popup button
 *  - native-cart sync (`nativeCart.ts`) → `cart:add` / `cart:remove` + the Discount API
 *
 * The merchant's thank-you HTML **is** rendered — as markdown, since no raw-HTML component exists.
 * See `thankYou.ts` for what survives the conversion.
 */
/**
 * Confirmation copy. Hard-coded, exactly as the legacy renderer had it — `MessagesConfig` covers
 * only the two validation strings, and adding four more is a schema change with an admin surface
 * behind it. Keeping the strings identical to the legacy is what matters while both ship.
 */
export const THANK_YOU = {
    title: 'Pedido confirmado',
    body: 'Nos pondremos en contacto para coordinar la entrega.',
    continueLabel: 'Continuar',
    whatsappLabel: 'Continuar en WhatsApp',
};
/** Reserved `FormState.values` keys for the two consent lines, which have no `FieldConfig.key`. */
export const TERMS_KEY = '__terms';
export const NEWSLETTER_KEY = '__newsletter';
function headerNode(config, styles) {
    if (!config.header.enabled)
        return [];
    const icon = config.header.show_icon ? homeIcon() : null;
    const title = text({ style: styles.header.title, children: [config.header.title] });
    return [
        box({
            style: styles.header.container,
            children: [
                icon
                    ? row({
                        style: { ...styles.buttonContent, color: config.appearance.text_color },
                        children: [icon, title],
                    })
                    : title,
            ],
        }),
    ];
}
/**
 * The × that dismisses the popup.
 *
 * 🚨 The SDK path had none. The legacy bundle has floated one in the panel's corner since it
 * shipped (`.cod-close`), and the stylesheet's own note claimed the SDK "needs no equivalent,
 * because the host owns the `modal_content` chrome" — but the host's dialog gives the buyer only a
 * backdrop tap and Escape. Escape needs a keyboard, and a backdrop tap is not an affordance
 * anybody can SEE, so on a phone the form read as something you could not get out of.
 *
 * Chrome, not header content: rendered as a sibling of the body column so hiding the Header field
 * cannot take the only way out with it. Not in EMBEDDED mode — that form is part of the page and
 * has nothing to dismiss.
 *
 * The handler is optional so the Designer preview draws the same corner the buyer sees, inert like
 * every other control there.
 */
const CLOSE_LABEL = 'Cerrar';
function closeNode(config, options, styles) {
    // `!== 'embedded'`, not `=== 'modal'`: `preview` must draw it too, or the Designer stops being a
    // faithful picture of the popup — `form.test.ts` asserts preview and storefront trees match.
    if (options.mode === 'embedded')
        return [];
    const onClose = options.handlers?.onClose;
    const StyledClose = styled(button) `
    ${codCloseCss(config)}
  `;
    return [
        StyledClose({
            key: 'close',
            ariaLabel: CLOSE_LABEL,
            style: styles.close,
            children: ['\u00d7'],
            ...(onClose ? { onClick: onClose } : {}),
        }),
    ];
}
/**
 * The terms line: a checkbox whose label is a link when the merchant set a URL.
 *
 * 🚨 **Every part of this row goes through `styled()`, not the style prop.** Read
 * `consentRowCss`'s header before touching any of it: with a URL set, the storefront put the
 * checkbox at the far left of an empty row and squeezed the link against the right edge, wrapped
 * and untappable, while the Designer preview — the same tree painted with Nimbus — looked fine.
 * The style prop only wins where the theme states nothing, and a storefront states plenty about
 * links and form controls.
 */
function termsNode(config, o, styles) {
    const terms = config.consent.terms;
    const checked = o.state.values[TERMS_KEY] === 'true';
    const label = terms.required ? `${terms.label} *` : terms.label;
    // A link cannot live inside the host's checkbox label, which is a plain string — so when there
    // is a URL the text becomes a sibling, and the box has to stop claiming the whole row.
    const linked = Boolean(terms.url);
    const StyledRow = styled(row) `
    ${consentRowCss()}
  `;
    const StyledBox = styled(checkbox) `
    ${consentBoxCss()}
  `;
    const StyledText = styled(box) `
    ${consentTextCss()}
  `;
    const StyledLink = styled(link) `
    ${termsLinkCss(config)}
  `;
    // Pinned ONLY when the text is a link. Unlinked, the checkbox carries the sentence and must be
    // free to take the row and wrap — see `consentRowCss`.
    const Box = linked ? StyledBox : checkbox;
    const boxStyle = linked
        ? {
            ...styles.checkbox,
            container: { ...styles.checkbox.container, flex: '0 0 auto', width: 'auto' },
            label: styles.terms.label,
        }
        : // 🚨 `styles.terms.label` carries the merchant's text colour and font size, and this used to
            // pass the bare `styles.checkbox` — whose own label slot is a hardcoded 14px in the global
            // appearance colour. So the Terms block's two style controls did nothing here, while the
            // legacy renderer applied both (`form.ts:1028-1029`): a divergence a merchant saw as "the
            // preview ignores my settings". Same shape as `newsletterNode` below.
            { ...styles.checkbox, label: styles.terms.label };
    return box({
        style: styles.field.container,
        children: [
            StyledRow({
                // `.cod-consent label { display:flex; align-items:flex-start; gap:10px }` — flex-START,
                // so a label that wraps to two lines keeps its box aligned with the first line.
                style: styles.consentRow,
                children: [
                    Box({
                        key: 'terms-box',
                        name: TERMS_KEY,
                        label: linked ? '' : label,
                        checked,
                        style: boxStyle,
                        ...(o.handlers?.onFieldChange
                            ? { onChange: () => o.handlers?.onFieldChange?.(TERMS_KEY, checked ? '' : 'true') }
                            : {}),
                    }),
                    ...(terms.url
                        ? [
                            StyledText({
                                key: 'terms-text',
                                style: styles.consentText,
                                children: [
                                    StyledLink({
                                        href: terms.url,
                                        target: '_blank',
                                        // Font size from the label, colour from the link — the same layering the
                                        // legacy gets for free by styling the <label> and overriding only the <a>'s
                                        // colour. Spread order matters: `link` must win on `color`. Kept as the
                                        // degraded fallback beside `termsLinkCss`, which is what actually lands.
                                        style: { ...styles.terms.label, ...styles.terms.link },
                                        children: [label],
                                    }),
                                ],
                            }),
                        ]
                        : []),
                ],
            }),
        ],
    });
}
function newsletterNode(config, o, styles) {
    const checked = o.state.values[NEWSLETTER_KEY] === 'true';
    return box({
        style: styles.field.container,
        children: [
            checkbox({
                name: NEWSLETTER_KEY,
                label: config.consent.newsletter.text,
                checked,
                style: { ...styles.checkbox, label: styles.newsletter.label },
                ...(o.handlers?.onFieldChange
                    ? { onChange: () => o.handlers?.onFieldChange?.(NEWSLETTER_KEY, checked ? '' : 'true') }
                    : {}),
            }),
        ],
    });
}
/**
 * The submit button.
 *
 * `{order_total}` is interpolated at render time, matching the legacy behaviour — the label is a
 * live price, so it has to be rebuilt on every state change rather than once.
 */
function submitNode(blockOpts) {
    const { config, build: o, styles } = blockOpts;
    // `{order_total}` here is what the buyer PAYS — merchandise − discount + shipping. The first port
    // interpolated the merchandise total, so the button and the summary disagreed whenever a paid
    // shipping rate was selected.
    const label = tokens(blockOpts, config.submit.text);
    // 🚨 Through `styled()`, not the style prop. `styles.buttonLabel` already carries
    // `margin: '0 !important'`, but inline `!important` does not beat a theme rule — measured on a
    // live storefront as `topGap 17 / bottomGap 29` inside symmetric 16px padding, the 12px gap being
    // the theme's `p { margin-bottom }` on our label. The flex container centres the label's MARGIN
    // box, so the visible text rode 12px high. See `formButtonLabelCss`.
    const color = config.submit.style.text_color;
    const StyledLabel = styled(text) `
    ${formButtonLabelCss(color)}
  `;
    const children = [StyledLabel({ style: styles.buttonLabel, children: [label] })];
    if (config.submit.subtitle) {
        const StyledSubtitle = styled(text) `
      ${buttonSubtitleCss(color)}
    `;
        children.push(StyledSubtitle({ style: styles.submitSubtitle, children: [config.submit.subtitle] }));
    }
    const props = {
        children: [
            column({
                style: { ...styles.buttonContent, color: config.submit.style.text_color },
                children,
            }),
        ],
        style: styles.submit,
        // Disabled while a submit is in flight, so a double-tap cannot create two orders.
        disabled: o.state.submitting,
        ...(o.handlers?.onSubmit ? { onClick: () => o.handlers?.onSubmit?.() } : {}),
    };
    // The merchant's `.cod-anim-*` animation, through `styled()` — and ONLY when they chose one, so
    // a config with no animation never depends on that channel.
    const animation = buttonAnimationCss(config.submit.style.animation);
    if (!animation)
        return button(props);
    const Animated = styled(button) `
    ${animation}
  `;
    return Animated(props);
}
/**
 * The confirmation panel shown after a successful order, and the handoff link when the merchant
 * configured one. Ports `showSuccess` / `showHandoff` / `showThankYou`
 * (`form-renderer/src/form.ts:1393-1465`).
 *
 * 🚨 **The merchant's page renders in ALL THREE modes, not only `page`.** The modes are named "show
 * a custom page" / "show a button to a URL" / "show a button to WhatsApp" — the button is the choice,
 * not a replacement for the confirmation. Treating them as exclusive threw the page away: a merchant
 * who wrote a page with the order number, total and address, then switched to the WhatsApp button,
 * got a bare "Pedido confirmado" and a button, and their content was still stored but unreachable.
 * So the page is the panel body whenever it exists, and the handoff sits under it.
 *
 * ⚠️ `url` and `whatsapp` render a **button the buyer taps** — they never navigate on their own.
 * A worker's `browser.navigate()` is same-origin only, so auto-redirecting would work on a legacy
 * theme and silently stop the moment the canary rollout moved the store. The legacy bundle was
 * converged to tap as well (`MIGRATION.md` §4.12 / §12.0), so both paths behave identically.
 *
 * ⚠️ **The host puts its own dialog in front of that tap** and we cannot suppress it: `nube-link`
 * shows a "you are about to leave {store}" confirmation for every cross-origin `href`, unconditional
 * and not configurable (measured in the host bundle — the copy ships in pt/es/en). It is one extra
 * tap on the SDK path that the legacy path does not have, and the merchant's own links inside
 * `page_html` get it too, since the host's markdown renderer builds them from the same component.
 */
/** The merchant's page with the tags stripped — the fallback when `htmlToTree` cannot read it. */
function markdownPage(blockOpts, html) {
    const content = thankYouMarkdown(blockOpts, html);
    return content ? markdown({ content }) : null;
}
function thankYouNode(blockOpts) {
    const { config, build: o, styles } = blockOpts;
    const ty = config.thank_you;
    const preview = o.mode === 'preview';
    const handoff = (label, href, style = styles.submit) => [link({ href, target: '_blank', style, children: [label] })];
    let extra = [];
    let mode = 'page';
    let page = null;
    if (!preview && ty?.enabled) {
        if (ty.mode === 'url' && ty.redirect_url.trim()) {
            mode = 'url';
            extra = handoff(THANK_YOU.continueLabel, ty.redirect_url.trim());
        }
        else if (ty.mode === 'whatsapp' && ty.whatsapp_phone.trim()) {
            mode = 'whatsapp';
            const message = thankYouTokens(blockOpts, ty.whatsapp_message);
            extra = handoff(THANK_YOU.whatsappLabel, whatsappHref(ty.whatsapp_phone, message), 
            // WhatsApp green rather than the merchant's submit colour: the buyer has already paid the
            // form's primary action, and this button hands them to a different app — the brand colour is
            // what makes that legible at a glance. `defaultActionButtonStyle` is the shade the in-form
            // WhatsApp block already defaults to, so the two agree and there is one literal.
            {
                ...styles.submit,
                backgroundColor: defaultActionButtonStyle.bg_color,
                color: defaultActionButtonStyle.text_color,
            });
        }
        // The tree keeps the merchant's design — cards, colours, icons. `markdown` is what is left
        // when the page is not markup this can read, and it renders the words with the tags stripped.
        const built = thankYouTree(blockOpts, ty.page_html) ?? markdownPage(blockOpts, ty.page_html);
        // Wrapped rather than dropped straight into the panel: `successPanel` centres both the text and
        // the box, and neither is right for a page the merchant laid out themselves. See
        // `styles.thankYouPage`.
        if (built)
            page = box({ direction: 'col', style: styles.thankYouPage, children: [built] });
    }
    o.handlers?.onThankYou?.(mode);
    return column({
        style: styles.successPanel,
        children: page
            ? // The merchant's page already carries its own heading and confirmation copy — adding our ✔
                // and "Pedido confirmado" above it would announce the order twice.
                [page, ...extra]
            : [
                text({ style: styles.successCheck, children: ['✔'] }),
                text({ style: styles.header.title, children: [THANK_YOU.title] }),
                ...(extra.length ? extra : [text({ style: styles.text, children: [THANK_YOU.body] })]),
            ],
    });
}
/** A form-level message (a failed order, or the consent warning) shown above submit. */
function messageNode(o, styles) {
    if (!o.state.message)
        return [];
    const style = o.state.message.kind === 'error' ? styles.message.error : styles.message.success;
    // Through `styled()` for the same reason the field errors are: a theme that margins paragraphs
    // with `!important` would win against the inline style, and here that margin lands INSIDE the
    // coloured box. See `messageTextCss`.
    const StyledMessage = styled(text) `
    ${messageTextCss()}
  `;
    return [
        box({
            style,
            children: [StyledMessage({ style: styles.text, children: [o.state.message.text] })],
        }),
    ];
}
/** One field entry → its node, or nothing when the block is not this phase's job. */
function entryNode(f, config, ctx, o, styles) {
    // Submit is exempt from `enabled` — the legacy loop skipped the check for it (`form.ts:1017`),
    // because a form with no way to submit is never what the merchant meant.
    if (!f.enabled && f.type !== 'submit')
        return [];
    const blockOpts = { config, ctx, build: o, styles };
    switch (f.type) {
        // NOTE: no `address_heading` case — the block type is retired. The default config ships the
        // same wording as a `custom_text` field, which falls through to `customTextNode` below.
        case 'terms':
            return [termsNode(config, o, styles)];
        case 'newsletter':
            return [newsletterNode(config, o, styles)];
        case 'order_summary':
            return [orderSummaryNode(blockOpts)];
        case 'discount_code':
            return [discountNode(blockOpts)];
        case 'totals_summary':
            return [totalsNode(blockOpts)];
        case 'quantity_offers': {
            // Nothing when no offer targets this product, when the "disable when qty exceeds" guard
            // suppressed it, or when it has no tiers. `display_above_buy_button` offers render from the
            // app into the product slot instead — they must not appear twice.
            const offer = o.offer;
            // `display_above_buy_button` only takes effect in POPUP mode, where there is a product-page
            // surface to put them above (`index.ts:116`). An embedded form IS the product-page surface,
            // so its offers render inline — otherwise the setting silently deletes the block.
            const above = offer?.design.display_above_buy_button && config.mode === 'popup';
            if (!offer || above)
                return [];
            const node = offersNode(blockOpts, offer);
            return node ? [node] : [];
        }
        case 'shipping_method': {
            // Null when rates are configured but none are eligible — the legacy hid the whole section.
            const node = shippingNode(blockOpts, o.eligibleRateIds);
            return node ? [node] : [];
        }
        case 'custom_text':
            return customTextNode(f, blockOpts);
        case 'custom_image':
            return customImageNode(f, blockOpts);
        case 'whatsapp_button':
            return whatsappNode(f, blockOpts);
        case 'link_button':
            return linkButtonNode(f, blockOpts);
        case 'submit':
            // Submit renders AT ITS CONFIGURED POSITION, not last.
            //
            // The first version forced it to the end, which put it *after* the WhatsApp and custom-text
            // blocks on a live store — the Designer shows submit, then WhatsApp, then the text. The
            // legacy renderer appends `submitBtn` inside the same field loop (`form.ts:1023`), so
            // position is the merchant's to choose like every other block.
            return [submitNode(blockOpts)];
        default:
            break;
    }
    // An input field. Coverage-driven fields become cascading selects; everything else is itself.
    const idx = chainIndex(config, f.key);
    if (idx >= 0 && o.coverage) {
        const options = coverageOptions(config, o.coverage, o.state, idx);
        return [
            fieldNode(f, {
                config,
                autoFocus: f.key === o.focusKey,
                state: {
                    ...o.state,
                    // A downstream value invalidated by an upstream change reads as empty (see coverage.ts).
                    values: { ...o.state.values, [f.key]: coverageValue(config, o.coverage, o.state, idx) },
                },
                styles,
                handlers: o.handlers,
                coverageOptions: options,
                disabled: options.length === 0,
            }),
        ];
    }
    return [
        fieldNode(f, {
            config,
            state: o.state,
            styles,
            handlers: o.handlers,
            autoFocus: f.key === o.focusKey,
        }),
    ];
}
/**
 * The COD open button, as a tree.
 *
 * 🚨 Shared for the same reason `buildCodForm` is: it is part of what the merchant DESIGNS — text,
 * icon, colours, radius, border — so a second copy in the admin would drift from the storefront's
 * the first time either changed. It lived in `apps/storefront-app/src/main.ts` and the Designer
 * preview simply had no button at all after the walker landed.
 *
 * Styled through BOTH channels, deliberately. The `style` prop carries the appearance and is known
 * to render; `styled()` carries only the theme defence (`!important` typography, the icon gap, the
 * padding a theme would otherwise force). Layered, never replaced — making the button's existence
 * depend on `styled()` once removed it from the storefront entirely.
 */
export function buildCodButton(config, opts = {}) {
    const styles = codStyles(config);
    const icon = buttonIcon(config.button.icon);
    const StyledButton = styled(button) `
    ${codButtonCss(config)}
  `;
    // The label carries its OWN reset rather than inheriting one from the button. `& *` is not a
    // legal selector here — the host throws `Selector "*" is not supported` and drops the whole
    // component — so every element that must resist the theme is styled individually.
    const StyledLabel = styled(text) `
    ${buttonLabelCss(config.button.text_color)}
  `;
    const label = StyledLabel({ children: [config.button.text], style: styles.buttonLabel });
    return withIdentity(StyledButton({
        children: [
            row({
                // The merchant's text colour lives HERE, not on the button: the label and the icon both
                // inherit it from a plain box the theme has no rule for.
                style: { ...styles.buttonContent, color: config.button.text_color },
                children: icon ? [icon, label] : [label],
            }),
        ],
        style: styles.openButton,
        ...(opts.onClick ? { onClick: opts.onClick } : {}),
    }), 
    // 🚨 The identity prefix is per-SURFACE, and it is the difference between two working buttons
    // and one that opens the wrong form.
    //
    // On a product page with a non-empty cart BOTH surfaces are live at once — the product slot's
    // button and the cart drawer's — and they are the same component: same label, same style, and
    // `onClick` closures written at the SAME source position, which the SDK stringifies by source
    // TEXT rather than identity. So they hashed to one `__internalId` (measured:
    // `button-test-app-1czmdo418anwys` for both), the host resolved the collision to the
    // last-registered handler, and the CART slot registers last (`owned = [...PRODUCT_SLOTS,
    // CART_SLOT]`). Pressing the button on the product page therefore opened the CART form: cart
    // lines only, page product absent, `cart_mode` never consulted — so "Product only" and
    // "Product + Cart" broke identically and the merchant saw a form describing a product they
    // were not looking at.
    //
    // 🚨 It only reproduces with something IN the cart, because `slotPlan` adds the cart slot on
    // `cartHasItems`. That is why it looked like it "used to work": an empty cart leaves one
    // button and nothing to collide with.
    `cod-btn-${opts.surface ?? 'product'}`);
}
/**
 * Build the COD form.
 *
 * Field ORDER is `config.fields` order — that is the whole point of modelling every block as a
 * field, and it is what the Designer's drag-to-reorder writes. The single exception is `submit`,
 * which always renders last: the legacy renderer did the same (`form.ts` always appended it), so a
 * merchant who drags submit into the middle still gets a form that ends with its call to action.
 */
export function buildCodForm(config, ctx, options) {
    const styles = codStyles(config);
    const blockOpts = { config, ctx, build: options, styles };
    // A completed order replaces the whole form with the confirmation panel, exactly as the legacy
    // `form.replaceChildren(...)` did. Without this branch the buyer pressed submit, the order was
    // created, and the form re-rendered looking untouched.
    //
    // ⚠️ This does NOT scroll itself into view the way the legacy `showPanel` does, because a worker
    // cannot measure the form's page offset and `browser.scrollTo()` only takes page pixels — the
    // full reasoning is on `BuildOptions.focusKey` in `builder.ts`. What an EMBEDDED order does
    // instead is paint this same panel a second time in the modal slot, where the buyer cannot miss
    // it: `embeddedConfirmation` in `storefront-app/src/main.ts`. That is why the branch below has
    // to draw the close button — see it.
    if (options.state.submitted) {
        return withIdentity(box({
            // The SAME panel the form used, chosen the same way. It used to hard-code `styles.panel`,
            // which handed an embedded thank-you the modal's scroll container, its heavy shadow and
            // (since the width was pinned) the modal's 460px — so a full-width embedded form snapped
            // to a narrow card the moment the order went through.
            style: options.mode === 'modal' ? styles.panel : styles.panelEmbedded,
            // 🚨 The × belongs on the confirmation too, not just on the form.
            //
            // The legacy bundle keeps it without trying: its close button is part of the modal chrome
            // OUTSIDE the `form` element that `showPanel` replaces, so a legacy popup buyer still has
            // a visible way out after ordering. Here the whole tree is swapped, so omitting it left an
            // SDK popup buyer with a backdrop tap and Escape — one invisible, the other absent on a
            // phone. `closeNode` returns nothing in embedded mode, so the inline panel is unaffected.
            children: [...closeNode(config, options, styles), thankYouNode(blockOpts)],
        }), 'cod-ty');
    }
    const body = config.fields.flatMap((f) => 
    // Key every block by the field it came from, so a block that appears or disappears (an
    // ineligible shipping section, a hidden totals box) does not shift the identity of every
    // block after it. See `identity.ts`.
    entryNode(f, config, ctx, options, styles).map((node) => keyed(node, `f-${f.key || f.type}`)));
    // A form must be submittable. The legacy loop rendered `submit` even when its entry was disabled
    // (`form.ts:1017` exempts it from the `enabled` check); this is the same guarantee for the case
    // where the config has no `submit` entry at all.
    const hasSubmit = config.fields.some((f) => f.type === 'submit');
    const fallbackSubmit = hasSubmit ? [] : [submitNode(blockOpts)];
    // 🚨 `withIdentity` is not optional polish — without it the tree is full of colliding
    // `__internalId`s and the form renders as a set of controls that look right and do the wrong
    // thing. Read the header of `identity.ts` before removing or reordering this.
    return withIdentity(box({
        // `padding: '0'` here was why the preview rendered edge to edge. Embedded and preview keep
        // the panel's padding; what they drop is the modal's scroll container and heavy shadow.
        style: options.mode === 'modal' ? styles.panel : styles.panelEmbedded,
        children: [
            ...closeNode(config, options, styles),
            column({
                key: 'body',
                // Fills the panel rather than shrinking to its content — see `panelBase`. Stated as well
                // as relying on the panel's `align-items: stretch`, because a percentage width inside a
                // content-sized parent is circular and resolves to max-content.
                style: { width: '100%', minWidth: '0' },
                children: [
                    ...headerNode(config, styles),
                    ...body,
                    ...messageNode(options, styles),
                    ...fallbackSubmit,
                ],
            }),
        ],
    }), 'cod');
}
/** Attach a key to an already-built node, for callers that build children in a loop. */
function keyed(node, key) {
    if (typeof node === 'object' && node !== null)
        node.key = key;
    return node;
}
//# sourceMappingURL=form.js.map